/**
 * QuizTake (Sprint 6 B4) — UI làm bài.
 *
 * Route: /quizzes/attempts/:attemptId/take
 * (Sprint 6 B5 sẽ wrap với ProctorWrapper để fullscreen + webcam.)
 *
 * UX:
 *   - Sticky header: title + QuizTimer + button Submit
 *   - Sidebar: question navigation (button số 1-N, xanh = answered)
 *   - Center: 1 question 1 lần với Radio/Checkbox
 *   - Auto-save mỗi 5s (debounce) + manual save on choice change
 *   - Auto-submit khi timer expire
 *   - beforeunload warn nếu IN_PROGRESS
 *
 * State per question:
 *   selections: Record<question_id, Set<choice_id>>
 *   syncedRev: Record<question_id, signature> — đã save lên server thành công
 */

import { useEffect, useRef, useState } from "react";
import {
  Affix,
  Alert,
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  Modal,
  Radio,
  Result,
  Row,
  Space,
  Spin,
  Typography,
  message,
} from "antd";
import { CheckOutlined, SendOutlined } from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MarkdownContent } from "@/components/MarkdownContent";
import { QuizTimer } from "@/components/QuizTimer";
import { ProctorWrapper } from "@/components/Proctoring";
import { useAnswerQuestion, useAttempt, useSubmitAttempt } from "@/api/assessment";

const { Title, Text } = Typography;

function signature(ids: number[]): string {
  return [...ids].sort((a, b) => a - b).join(",");
}

export function QuizTake() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const aid = attemptId ? Number(attemptId) : undefined;
  const { data: attempt, isLoading, error } = useAttempt(aid);
  const answerMut = useAnswerQuestion(aid);
  const submitMut = useSubmitAttempt();

  // ─── Local selection state (per question) ───────────────────────────
  const [selections, setSelections] = useState<Record<number, Set<number>>>({});
  const [syncedRev, setSyncedRev] = useState<Record<number, string>>({});
  // short_answer/essay — text riêng, tách khỏi `selections` (MC/TF dùng choice id).
  const [textAnswers, setTextAnswers] = useState<Record<number, string>>({});
  const [textSyncedRev, setTextSyncedRev] = useState<Record<number, string>>({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Init selections từ server state (lần load đầu hoặc reload giữa bài)
  useEffect(() => {
    if (!attempt) return;
    const initial: Record<number, Set<number>> = {};
    const initialSync: Record<number, string> = {};
    const initialText: Record<number, string> = {};
    const initialTextSync: Record<number, string> = {};
    for (const [qid, snap] of Object.entries(attempt.answers)) {
      const ids = snap.selected_choice_ids ?? [];
      initial[Number(qid)] = new Set(ids);
      initialSync[Number(qid)] = signature(ids);
      initialText[Number(qid)] = snap.text_answer ?? "";
      initialTextSync[Number(qid)] = snap.text_answer ?? "";
    }
    setSelections(initial);
    setSyncedRev(initialSync);
    setTextAnswers(initialText);
    setTextSyncedRev(initialTextSync);
  }, [attempt]);

  // Beforeunload warn khi IN_PROGRESS
  useEffect(() => {
    if (!attempt || attempt.status !== "in_progress") return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [attempt]);

  // ─── Auto-save mỗi 5s — sync mọi question chưa synced ───────────────
  const selectionsRef = useRef(selections);
  const syncedRevRef = useRef(syncedRev);
  const textAnswersRef = useRef(textAnswers);
  const textSyncedRevRef = useRef(textSyncedRev);
  useEffect(() => {
    selectionsRef.current = selections;
  }, [selections]);
  useEffect(() => {
    syncedRevRef.current = syncedRev;
  }, [syncedRev]);
  useEffect(() => {
    textAnswersRef.current = textAnswers;
  }, [textAnswers]);
  useEffect(() => {
    textSyncedRevRef.current = textSyncedRev;
  }, [textSyncedRev]);

  useEffect(() => {
    if (!aid || !attempt || attempt.status !== "in_progress") return;
    const id = setInterval(async () => {
      for (const [qidStr, set] of Object.entries(selectionsRef.current)) {
        const qid = Number(qidStr);
        const sig = signature([...set]);
        if (syncedRevRef.current[qid] === sig) continue;
        try {
          await answerMut.mutateAsync({
            question_id: qid,
            selected_choice_ids: [...set],
          });
          setSyncedRev((prev) => ({ ...prev, [qid]: sig }));
        } catch {
          // Ignore — retry trong lần tick sau
        }
      }
      for (const [qidStr, text] of Object.entries(textAnswersRef.current)) {
        const qid = Number(qidStr);
        if (textSyncedRevRef.current[qid] === text) continue;
        try {
          await answerMut.mutateAsync({ question_id: qid, text_answer: text });
          setTextSyncedRev((prev) => ({ ...prev, [qid]: text }));
        } catch {
          // Ignore — retry trong lần tick sau
        }
      }
    }, 5_000);
    return () => clearInterval(id);
  }, [aid, attempt, answerMut]);

  // ─── Loading / error ──────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (error || !attempt) {
    return (
      <Result
        status="404"
        title="Không tìm thấy bài làm"
        extra={<Link to="/my-courses">← Về môn học của tôi</Link>}
      />
    );
  }

  // Nếu đã submit → redirect sang review
  if (attempt.status !== "in_progress") {
    return (
      <Result
        status="success"
        title="Bài thi đã kết thúc"
        subTitle={`Trạng thái: ${attempt.status}`}
        extra={
          <Button type="primary" onClick={() => navigate(`/quizzes/attempts/${attempt.id}/review`)}>
            Xem kết quả
          </Button>
        }
      />
    );
  }

  const questions = [...attempt.questions].sort((a, b) => a.order - b.order);
  const currentQ = questions[currentIdx];
  const totalQ = questions.length;
  const isAnswered = (q: (typeof questions)[number]): boolean => {
    if (q.type === "short_answer" || q.type === "essay") {
      return (textAnswers[q.id] ?? "").trim().length > 0;
    }
    return (selections[q.id]?.size ?? 0) > 0;
  };
  const answeredCount = questions.filter(isAnswered).length;

  const isChoiceSelected = (questionId: number, choiceId: number): boolean => {
    return selections[questionId]?.has(choiceId) ?? false;
  };

  const toggleChoice = (questionId: number, choiceId: number, multiSelect: boolean) => {
    setSelections((prev) => {
      const next = new Set(prev[questionId] ?? []);
      if (multiSelect) {
        if (next.has(choiceId)) next.delete(choiceId);
        else next.add(choiceId);
      } else {
        next.clear();
        next.add(choiceId);
      }
      return { ...prev, [questionId]: next };
    });
  };

  const handleSubmit = async () => {
    Modal.confirm({
      title: "Nộp bài thi?",
      content: `Bạn đã trả lời ${answeredCount}/${totalQ} câu. Sau khi nộp không thể sửa đáp án.`,
      okText: "Nộp bài",
      okType: "primary",
      cancelText: "Quay lại",
      onOk: async () => {
        setSubmitting(true);
        try {
          // Force flush pending answers trước submit
          for (const [qidStr, set] of Object.entries(selections)) {
            const qid = Number(qidStr);
            const sig = signature([...set]);
            if (syncedRev[qid] !== sig) {
              try {
                await answerMut.mutateAsync({
                  question_id: qid,
                  selected_choice_ids: [...set],
                });
              } catch {
                /* swallow */
              }
            }
          }
          for (const [qidStr, text] of Object.entries(textAnswers)) {
            const qid = Number(qidStr);
            if (textSyncedRev[qid] !== text) {
              try {
                await answerMut.mutateAsync({ question_id: qid, text_answer: text });
              } catch {
                /* swallow */
              }
            }
          }
          await submitMut.mutateAsync(attempt.id);
          message.success("Nộp bài thành công!");
          navigate(`/quizzes/attempts/${attempt.id}/review`);
        } catch {
          message.error("Nộp bài thất bại. Vui lòng thử lại.");
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const handleTimerExpire = async () => {
    message.warning("Hết thời gian — tự động nộp bài.");
    setSubmitting(true);
    try {
      await submitMut.mutateAsync(attempt.id);
      navigate(`/quizzes/attempts/${attempt.id}/review`);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────
  // Wrap với ProctorWrapper nếu có session (B5/B6) — fullscreen + tab-switch +
  // webcam + paste-block. Attempt cũ không có proctor_session_id → render
  // without proctoring (graceful degrade cho data migration period).
  const content = (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/my-courses">Khóa của tôi</Link> },
          { title: attempt.quiz.title },
          { title: "Đang làm bài" },
        ]}
      />

      {/* Sticky header với timer + submit */}
      <Affix offsetTop={0}>
        <Card
          size="small"
          style={{ marginBottom: 16, background: "#fff" }}
          data-testid="quiz-take-header"
        >
          <Row align="middle" justify="space-between" gutter={16}>
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                {attempt.quiz.title}
              </Title>
              <Text type="secondary">
                Đã trả lời{" "}
                <strong>
                  {answeredCount}/{totalQ}
                </strong>
              </Text>
            </Col>
            <Col>
              <Space size="middle">
                {attempt.deadline_at && (
                  <QuizTimer deadline={attempt.deadline_at} onExpire={handleTimerExpire} />
                )}
                <Button
                  type="primary"
                  size="large"
                  icon={<SendOutlined />}
                  loading={submitting}
                  onClick={handleSubmit}
                >
                  Nộp bài
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      </Affix>

      <Row gutter={16}>
        {/* Sidebar: question nav */}
        <Col xs={24} md={6}>
          <Card title="Câu hỏi" size="small">
            <Space wrap data-testid="question-nav">
              {questions.map((q, idx) => {
                const answered = isAnswered(q);
                return (
                  <Button
                    key={q.id}
                    type={idx === currentIdx ? "primary" : "default"}
                    shape="circle"
                    size="small"
                    icon={answered ? <CheckOutlined /> : undefined}
                    onClick={() => setCurrentIdx(idx)}
                  >
                    {answered ? "" : idx + 1}
                  </Button>
                );
              })}
            </Space>
          </Card>
        </Col>

        {/* Center: current question */}
        <Col xs={24} md={18}>
          <Card
            title={`Câu ${currentIdx + 1} / ${totalQ} (${currentQ.points} điểm)`}
            data-testid="current-question"
            actions={[
              <Button
                key="prev"
                disabled={currentIdx === 0}
                onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
              >
                ← Câu trước
              </Button>,
              <Button
                key="next"
                type="primary"
                disabled={currentIdx === totalQ - 1}
                onClick={() => setCurrentIdx((i) => Math.min(totalQ - 1, i + 1))}
              >
                Câu sau →
              </Button>,
            ]}
          >
            <MarkdownContent source={currentQ.prompt} />
            <div style={{ marginTop: 24 }}>
              {currentQ.type === "multiple_choice" && (
                <Space direction="vertical" style={{ width: "100%" }}>
                  {[...currentQ.choices]
                    .sort((a, b) => a.order - b.order)
                    .map((c) => (
                      <Checkbox
                        key={c.id}
                        checked={isChoiceSelected(currentQ.id, c.id)}
                        onChange={() => toggleChoice(currentQ.id, c.id, true)}
                      >
                        {c.text}
                      </Checkbox>
                    ))}
                </Space>
              )}
              {currentQ.type === "true_false" && (
                <Radio.Group
                  value={[...(selections[currentQ.id] ?? new Set())][0]}
                  onChange={(e) => toggleChoice(currentQ.id, e.target.value, false)}
                >
                  <Space direction="vertical">
                    {[...currentQ.choices]
                      .sort((a, b) => a.order - b.order)
                      .map((c) => (
                        <Radio key={c.id} value={c.id}>
                          {c.text}
                        </Radio>
                      ))}
                  </Space>
                </Radio.Group>
              )}
              {(currentQ.type === "short_answer" || currentQ.type === "essay") && (
                <>
                  <Input.TextArea
                    value={textAnswers[currentQ.id] ?? ""}
                    onChange={(e) =>
                      setTextAnswers((prev) => ({ ...prev, [currentQ.id]: e.target.value }))
                    }
                    rows={currentQ.type === "essay" ? 10 : 3}
                    placeholder={
                      currentQ.type === "essay"
                        ? "Nhập câu trả lời tự luận của bạn..."
                        : "Nhập câu trả lời ngắn..."
                    }
                    maxLength={currentQ.type === "essay" ? 5000 : 500}
                    showCount
                  />
                  <Alert
                    style={{ marginTop: 12 }}
                    type="info"
                    showIcon
                    message="Câu này do giảng viên chấm sau khi nộp bài — điểm sẽ cập nhật khi có kết quả."
                  />
                </>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );

  if (attempt.proctor_session_id && attempt.consent_given_at) {
    return (
      <ProctorWrapper
        sessionId={attempt.proctor_session_id}
        webcamRequired={attempt.quiz.webcam_required}
      >
        {content}
      </ProctorWrapper>
    );
  }
  return content;
}

export default QuizTake;
